import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Globe,
  Handshake,
  Wheat,
  Ship,
  Network,
  Package,
  MapPin,
  HeartHandshake,
  MessageCircle,
} from "lucide-react";
import { PageMeta } from "../components/PageMeta";
import { MaskedLines, Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { Marquee } from "../components/Marquee";
import { WorldMap } from "../components/WorldMap";
import { ProductCard } from "../components/ProductCard";
import { useEnquiry } from "../components/EnquiryModal";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES, PRODUCTS, CONTACT } from "../data/content";

const TRUST_ICONS = [Globe, Handshake, Wheat, Ship];
const WHY_ICONS = [Network, Package, Handshake, MapPin, Globe, HeartHandshake];

const Hero = () => {
  const ref = useRef(null);
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-center overflow-hidden bg-[#09121F]">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="Container vessel carrying food commodities across international trade routes"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#09121F]/80 via-[#09121F]/55 to-[#09121F]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1D16]/85 via-[#09121F]/40 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 opacity-[0.16]">
        <WorldMap />
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-40 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="flex items-center gap-4"
        >
          <span className="h-px w-12 bg-amber-500" />
          <p className="text-[11px] uppercase tracking-[0.32em] font-display font-semibold text-amber-400 sm:text-xs">
            {t("hero.overline")}
          </p>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="mt-8 font-serif font-semibold leading-[1.02] tracking-tight text-emerald-50 text-5xl sm:text-6xl lg:text-8xl"
        >
          <MaskedLines
            lines={[
              t("hero.line1"),
              <span key="l2">
                {t("hero.line2a")} <span className="italic text-gold-gradient">{t("hero.line2b")}</span>
              </span>,
              t("hero.line3"),
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-slate-300/90 sm:text-lg font-light"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/products"
            data-testid="hero-explore-products-btn"
            className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-br from-[#DFB756] to-[#C49A2D] px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-[#0B1D16] transition-transform duration-300 hover:scale-105"
          >
            {t("hero.ctaProducts")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-contact-btn"
            className="inline-flex items-center gap-3 rounded-full border border-emerald-50/25 px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-emerald-50 backdrop-blur-sm transition-colors duration-300 hover:border-amber-400 hover:text-amber-400"
          >
            {t("hero.ctaContact")}
          </Link>
          <Link
            to="/global-reach"
            data-testid="hero-trade-map-btn"
            className="group inline-flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-amber-400"
          >
            {t("hero.ctaMap")}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex h-12 w-7 items-start justify-center rounded-full border border-emerald-50/25 p-2">
          <motion.span
            animate={{ y: [0, 14, 0], opacity: [1, 0.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="h-2 w-1 rounded-full bg-amber-400"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default function Home() {
  const { open } = useEnquiry();
  const { t } = useLanguage();
  const trustCards = t("trust.cards");
  const whyCards = t("why.cards");

  return (
    <main>
      <PageMeta
        title="Al Nama Importer | Food Trading Company Dubai – Food Import Export UAE"
        description="Al Nama Importer is a Dubai-based international food trading company importing and exporting cereals, spices, pulses, agro commodities, nuts and dry fruits across the UAE, Africa and global markets."
        titleAr="النعم للاستيراد | شركة تجارة مواد غذائية في دبي – استيراد وتصدير الأغذية الإمارات"
        descriptionAr="النعم للاستيراد شركة تجارة غذائية دولية مقرها دبي، تستورد وتصدر الحبوب والتوابل والبقوليات والسلع الزراعية والمكسرات والفواكه المجففة إلى الإمارات وأفريقيا والأسواق العالمية."
      />
      <Hero />
      <Marquee />

      <section className="bg-[#F4EFE6] py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            dark
            overline={t("trust.overline")}
            title={
              <>
                {t("trust.titleA")} <span className="italic text-[#8a6d1f]">{t("trust.titleB")}</span>
              </>
            }
            text={t("trust.text")}
          />
          <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {trustCards.map((c, i) => {
              const Icon = TRUST_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div
                    data-testid={`trust-card-${i}`}
                    className="group h-full rounded-lg border border-[#0B1D16]/10 bg-white/60 p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-amber-600/40 hover:shadow-[0_18px_40px_rgba(11,29,22,0.12)]"
                  >
                    <Icon className="h-7 w-7 text-[#8a6d1f]" strokeWidth={1.4} />
                    <h3 className="mt-5 font-serif text-xl font-semibold text-[#0B1D16]">{c.t}</h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-[#0B1D16]/65">{c.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#09121F] py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading
              overline={t("homeProducts.overline")}
              title={
                <>
                  {t("homeProducts.titleA")} <span className="italic text-gold-gradient">{t("homeProducts.titleB")}</span>
                </>
              }
              text={t("homeProducts.text")}
            />
            <Reveal delay={0.2}>
              <Link
                to="/products"
                data-testid="home-view-all-products-btn"
                className="group inline-flex items-center gap-3 rounded-full border border-amber-500/40 px-7 py-3.5 font-display text-xs font-semibold uppercase tracking-[0.18em] text-amber-400 transition-colors duration-300 hover:bg-amber-500/10"
              >
                {t("homeProducts.viewAll")}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.1}>
                <ProductCard product={p} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#0B1D16] py-24 sm:py-32">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-amber-500/5 blur-3xl" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading
            overline={t("why.overline")}
            title={
              <>
                {t("why.titleA")} <span className="italic text-gold-gradient">{t("why.titleB")}</span>
              </>
            }
          />
          <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {whyCards.map((c, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={i} delay={(i % 3) * 0.08}>
                  <div
                    data-testid={`why-card-${i}`}
                    className="group h-full rounded-lg border border-amber-500/15 bg-[#0F1E18]/60 p-8 transition-all duration-500 hover:border-amber-500/45 hover:bg-[#0F1E18]"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border border-amber-500/30 text-amber-400 transition-colors duration-500 group-hover:bg-amber-500/10">
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-serif text-xl font-semibold text-emerald-50">{c.t}</h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-slate-300/80">{c.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-28 sm:py-36">
        <div className="absolute inset-0">
          <img src={IMAGES.containers} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#09121F]/90" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09121F] via-transparent to-[#09121F]" />
        </div>
        <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] font-display font-semibold text-amber-400">
              {t("cta.overline")}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-serif text-3xl font-semibold leading-[1.12] tracking-tight text-emerald-50 sm:text-5xl">
              {t("cta.titleA")} <span className="italic text-gold-gradient">{t("cta.titleB")}</span>
              {t("cta.titleC")}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-xl text-base font-light leading-relaxed text-slate-300/90 sm:text-lg">
              {t("cta.text")}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <button
                data-testid="home-cta-send-enquiry-btn"
                onClick={() => open()}
                className="inline-flex items-center gap-3 rounded-full bg-gradient-to-br from-[#DFB756] to-[#C49A2D] px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-[#0B1D16] transition-transform duration-300 hover:scale-105"
              >
                {t("cta.send")}
                <ArrowUpRight className="h-4 w-4" />
              </button>
              <a
                data-testid="home-cta-whatsapp-btn"
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-full border border-[#1EBE5D]/60 px-8 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-[#4ce08a] transition-colors duration-300 hover:bg-[#1EBE5D]/10"
              >
                <MessageCircle className="h-4 w-4" />
                {t("cta.whatsapp")}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
